import {
  type DeviceElement,
  type DeviceFace,
  type DeviceFacePart,
  type DeviceGradient,
  type DevicePaint,
  type DeviceRadius,
  type DeviceShape,
  layoutDevice,
  roundedRectPath,
} from "@cva/shared";
import type { CSSProperties, ReactNode } from "react";

/**
 * Device mockups (docs/19-mockups.md): the layout from `@cva/shared` gives
 * the outlines of the sides (2D paths) and the faces with their CSS
 * transform; each face is an SVG drawing with the screenshot as an `<img>`
 * in its screen, so exports load and colour-convert it like any image.
 *
 * Video clips in a device put their own node on the screen (`screen`, the
 * editor's canvas); the video export renders the device in layers instead
 * (`layer`) and lays the warped video between them with ffmpeg.
 */

/** What a device needs of its element (device elements, clips in a device). */
export type DevicePartsElement = Pick<
  DeviceElement,
  "id" | "width" | "height" | "device" | "finish" | "landscape" | "tilt" | "url"
> &
  Partial<Pick<DeviceElement, "fit" | "glare">>;

/**
 * Part of the device to draw: `all`; `below` everything up to the screen,
 * which stays black; `above` what lies on the screen (Dynamic Island, notch,
 * glare) and in front of it; `screen` only the screen's shape in white (a
 * mask).
 */
export type DeviceLayer = "all" | "below" | "above" | "screen";

export interface DeviceScreenAsset {
  url: string;
  width?: number | null | undefined;
  height?: number | null | undefined;
}

/** Screen without a screenshot: a dark, slightly lit glass. */
const EMPTY_SCREEN = "linear-gradient(155deg, #3a3a42 0%, #1c1c21 55%, #111114 100%)";
const GLARE =
  "linear-gradient(118deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.07) 38%, rgba(255,255,255,0) 38.2%, rgba(255,255,255,0) 100%)";

/** Object-bounding-box line of a CSS-style angle (0 = bottom → top, 90 = left → right). */
function gradientLine(angle: number) {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.sin(rad) / 2;
  const dy = -Math.cos(rad) / 2;
  return { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy };
}

function paintFor(paint: DevicePaint | "none", id: string, defs: ReactNode[]): string {
  if (typeof paint === "string") return paint;
  const gradient: DeviceGradient = paint;
  defs.push(
    <linearGradient
      key={id}
      id={id}
      gradientUnits="objectBoundingBox"
      {...gradientLine(gradient.angle)}
    >
      {gradient.stops.map(([offset, color]) => (
        <stop key={offset} offset={offset} stopColor={color} />
      ))}
    </linearGradient>,
  );
  return `url(#${id})`;
}

function shapeNode(shape: DeviceShape, key: string, defs: ReactNode[]): ReactNode {
  const stroke =
    "stroke" in shape && shape.stroke
      ? { stroke: shape.stroke.color, strokeWidth: shape.stroke.width }
      : {};
  switch (shape.kind) {
    case "rect": {
      const fill = paintFor(shape.fill, `${key}-g`, defs);
      if (Array.isArray(shape.radius)) {
        return (
          <path
            key={key}
            d={roundedRectPath(shape.x, shape.y, shape.width, shape.height, shape.radius)}
            fill={fill}
            {...stroke}
          />
        );
      }
      return (
        <rect
          key={key}
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          rx={shape.radius || undefined}
          fill={fill}
          {...stroke}
        />
      );
    }
    case "circle":
      return (
        <circle
          key={key}
          cx={shape.cx}
          cy={shape.cy}
          r={shape.r}
          fill={paintFor(shape.fill, `${key}-g`, defs)}
          {...stroke}
        />
      );
    case "path":
      return (
        <path
          key={key}
          d={shape.d}
          fill={shape.fill === "none" ? "none" : paintFor(shape.fill, `${key}-g`, defs)}
          strokeLinecap="round"
          strokeLinejoin="round"
          {...stroke}
        />
      );
    case "text":
      return (
        <text
          key={key}
          x={shape.x}
          y={shape.y}
          fontSize={shape.size}
          fontFamily='"Inter", system-ui, sans-serif'
          fontWeight={shape.weight ?? 400}
          textAnchor="middle"
          fill={shape.fill}
        >
          {shape.text}
        </text>
      );
  }
}

/** One layer of a face (below or above the screen) as an SVG in face units. */
function FaceSvg({ face, shapes, id }: { face: DeviceFace; shapes: DeviceShape[]; id: string }) {
  if (shapes.length === 0) return null;
  const defs: ReactNode[] = [];
  const nodes = shapes.map((shape, index) => shapeNode(shape, `${id}-${index}`, defs));
  return (
    <svg
      viewBox={`0 0 ${face.width} ${face.height}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      style={{ position: "absolute", inset: 0, display: "block", overflow: "visible" }}
      aria-hidden="true"
    >
      {defs.length > 0 ? <defs>{defs}</defs> : null}
      {face.rotated ? <g transform={`translate(0 ${face.height}) rotate(-90)`}>{nodes}</g> : nodes}
    </svg>
  );
}

function radiusCss(radius: DeviceRadius, scale: number): string | undefined {
  if (typeof radius === "number") return radius > 0 ? `${radius * scale}px` : undefined;
  return radius.map((value) => `${value * scale}px`).join(" ");
}

function Face({
  face,
  width,
  height,
  transform,
  element,
  asset,
  content,
  layer,
  id,
}: {
  face: DeviceFace;
  width: number;
  height: number;
  transform: string;
  element: DevicePartsElement;
  asset: DeviceScreenAsset | undefined;
  content: ReactNode | undefined;
  layer: DeviceLayer;
  id: string;
}) {
  const screen = face.screen;
  const scale = width / face.width;
  const glare = Boolean(element.glare) && layer !== "below" && layer !== "screen";
  let screenNode: ReactNode = null;
  if (screen && (layer !== "above" || glare)) {
    const style: CSSProperties = {
      position: "absolute",
      left: `${(screen.x / face.width) * 100}%`,
      top: `${(screen.y / face.height) * 100}%`,
      width: `${(screen.width / face.width) * 100}%`,
      height: `${(screen.height / face.height) * 100}%`,
      overflow: "hidden",
      borderRadius: radiusCss(screen.radius, scale),
      background:
        layer === "screen"
          ? "#ffffff"
          : layer === "above"
            ? "transparent"
            : asset || content !== undefined || layer === "below"
              ? "#000000"
              : EMPTY_SCREEN,
    };
    screenNode = (
      <div data-device-screen="" style={style}>
        {layer !== "all" ? null : content !== undefined ? (
          content
        ) : asset ? (
          <img
            src={asset.url}
            alt=""
            draggable={false}
            decoding="sync"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: element.fit ?? "cover",
              objectPosition: screen.align === "top" ? "center top" : "center",
              display: "block",
            }}
          />
        ) : null}
        {glare ? (
          <div
            style={{ position: "absolute", inset: 0, background: GLARE, pointerEvents: "none" }}
          />
        ) : null}
      </div>
    );
  }
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width,
        height,
        transformOrigin: "0 0",
        transform,
      }}
    >
      {layer === "all" || layer === "below" ? (
        <FaceSvg face={face} shapes={face.shapes} id={`${id}-s`} />
      ) : null}
      {screenNode}
      {layer === "all" || layer === "above" ? (
        <FaceSvg face={face} shapes={face.overlay} id={`${id}-o`} />
      ) : null}
    </div>
  );
}

/** The parts of a device inside its element box (back to front). */
export function DeviceParts({
  element,
  asset,
  idPrefix,
  screen,
  contentAspect,
  layer = "all",
}: {
  element: DevicePartsElement;
  asset: DeviceScreenAsset | undefined;
  idPrefix: string;
  /** Shown on the screen instead of the screenshot (fills it, clipped to its shape). */
  screen?: ReactNode;
  /** Height / width of the screen content (browser windows); default: the asset's. */
  contentAspect?: number | null;
  layer?: DeviceLayer;
}) {
  const aspect =
    contentAspect !== undefined
      ? contentAspect
      : asset?.width && asset.height
        ? asset.height / asset.width
        : null;
  const layout = layoutDevice(element, { width: element.width, height: element.height }, aspect);
  // Layers split the parts at the face with the screen.
  const screenIndex = layout.parts.findIndex(
    (part) => part.kind === "face" && part.face.screen !== null,
  );
  const shows = (index: number, part: (typeof layout.parts)[number]) => {
    if (layer === "all") return true;
    if (screenIndex < 0) return layer === "below";
    if (layer === "below") return index <= screenIndex;
    if (layer === "above") return index >= screenIndex;
    return index === screenIndex && part.kind === "face";
  };
  return (
    <>
      {layout.parts.map((part, index) => {
        if (!shows(index, part)) return null;
        if (part.kind === "outline") {
          return (
            <svg
              // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional
              key={index}
              width="100%"
              height="100%"
              style={{ position: "absolute", inset: 0, display: "block", overflow: "visible" }}
              aria-hidden="true"
            >
              <path d={part.d} fill={part.fill} />
            </svg>
          );
        }
        const face: DeviceFacePart = part;
        // Parts behind the screen belong to the lower layer as a whole, parts in front to the upper.
        const partLayer =
          (layer === "above" && index > screenIndex) || (layer === "below" && index < screenIndex)
            ? "all"
            : layer;
        return (
          <Face
            // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional
            key={index}
            face={face.face}
            width={face.width}
            height={face.height}
            transform={face.transform}
            element={element}
            asset={asset}
            content={index === screenIndex ? screen : undefined}
            layer={partLayer}
            id={`${idPrefix}-${element.id}-${index}`}
          />
        );
      })}
    </>
  );
}
